"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";

interface IdleTimeoutModalProps {
  timeoutMinutes?: number;
  warningSeconds?: number;
}

export default function IdleTimeoutModal({
  timeoutMinutes = 5,
  warningSeconds = 60,
}: IdleTimeoutModalProps) {
  const { isAuthenticated, logout } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(warningSeconds);

  const lastActivityRef = useRef<number>(0);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isModalOpenRef = useRef(false);

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const totalTimeoutMs = timeoutMs + warningSeconds * 1000; // timeout + warning

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const clearAllTimers = useCallback(() => {
    clearCountdown();
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, [clearCountdown]);

  const handleLogout = useCallback(async () => {
    clearAllTimers();
    setShowModal(false);
    isModalOpenRef.current = false;
    await logout();
  }, [clearAllTimers, logout]);

  const startCountdown = useCallback(() => {
    setCountdown(warningSeconds);
    clearCountdown();

    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningSeconds, handleLogout, clearCountdown]);

  const openWarningModal = useCallback(() => {
    if (isModalOpenRef.current) return;
    isModalOpenRef.current = true;
    setShowModal(true);
    startCountdown();
  }, [startCountdown]);

  // Core logic: decide what to do based on how long the user was idle
  const checkIdle = useCallback(() => {
    if (!isAuthenticated || isModalOpenRef.current) return;

    const idleTime = Date.now() - lastActivityRef.current;

    if (idleTime >= totalTimeoutMs) {
      // User was away longer than timeout + warning → logout immediately
      handleLogout();
    } else if (idleTime >= timeoutMs) {
      // User crossed the idle timeout → show warning modal
      openWarningModal();
    }
  }, [
    isAuthenticated,
    timeoutMs,
    totalTimeoutMs,
    openWarningModal,
    handleLogout,
  ]);

  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    clearCountdown();
    setShowModal(false);
    isModalOpenRef.current = false;
    setCountdown(warningSeconds);
  }, [clearCountdown, warningSeconds]);

  const handleStay = () => {
    resetTimers();
  };

  // Main effect
  useEffect(() => {
    if (!isAuthenticated) {
      clearAllTimers();
      isModalOpenRef.current = false;
      return;
    }

    const handleActivity = () => {
      if (isModalOpenRef.current) return;
      lastActivityRef.current = Date.now();
    };

    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Check every 3–5 seconds (good balance)
    checkIntervalRef.current = setInterval(checkIdle, 4000);

    // Check immediately when user returns to the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkIdle();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Start tracking
    lastActivityRef.current = Date.now();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearAllTimers();
    };
  }, [isAuthenticated, checkIdle, clearAllTimers]);

  if (!isAuthenticated || !showModal) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgba(45,101,242,0.12)] border border-[#B8CBFC] w-full max-w-[420px] overflow-hidden">
        {/* Header */}
        <div className="bg-[#F5F9FC] px-6 py-5 border-b border-[#B8CBFC]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#2D65F2]/10 flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#2D65F2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <div>
              <h3 className="text-[18px] font-bold text-[#303030] font-openSans">
                Session Timeout Warning
              </h3>
              <p className="text-[13px] text-[#6F6464] mt-0.5">
                Inactivity detected
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          <p className="text-[15px] text-[#303030] leading-relaxed mb-5">
            You’ve been inactive for a while. For security reasons, your session
            will expire soon.
          </p>

          {/* Countdown Box */}
          <div className="bg-[#F5F9FC] border border-[#B8CBFC] rounded-xl p-4 flex items-center justify-between mb-6">
            <div>
              <p className="text-[12px] font-medium text-[#6F6464] uppercase tracking-wide">
                Auto logout in
              </p>
              <p className="text-[22px] font-bold text-[#2D65F2] mt-0.5">
                {countdown}{" "}
                <span className="text-[14px] font-medium text-[#6F6464]">
                  seconds
                </span>
              </p>
            </div>

            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-[#E5E7EB]"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-[#2D65F2]"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray={`${(countdown / warningSeconds) * 100}, 100`}
                  strokeLinecap="round"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleLogout}
              className="flex-1 border border-[#B8CBFC] rounded-lg py-2.5 px-4 text-[14px] font-semibold text-[#2D65F2] hover:bg-[#F5F9FC] transition-all duration-200"
            >
              Log me out
            </button>

            <button
              onClick={handleStay}
              className="flex-1 bg-[#2D65F2] text-white rounded-lg py-2.5 px-4 text-[14px] font-semibold hover:bg-[#1e4ed8] transition-all duration-200 shadow-[0_2px_8px_rgba(45,101,242,0.25)]"
            >
              Stay Logged In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
